import { useState, useEffect } from "react";
import { adminFetch } from "../../lib/adminApi";
import { BarChart3, Users, Eye, Globe, Languages, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface Metric {
  hits: number;
  uniques: number;
}

interface AnalyticsData {
  summary: {
    last7Days: Metric;
    last30Days: Metric;
  };
  topPages: { page_path: string; count: number }[];
  countries: { country: string; count: number }[];
  locales: { locale: string; count: number }[];
  timeline: { date: string; hits: number; uniques: number }[];
}

function StatCard({ title, value, icon: Icon, color }: { title: string; value: string | number; icon: any; color: string }) {
  return (
    <div className="p-6 border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl shadow-sm flex items-center gap-4">
      <div className={`p-3.5 rounded-xl ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</p>
        <h4 className="text-2xl font-bold mt-1 text-foreground">{value}</h4>
      </div>
    </div>
  );
}

function CountryList({ countries }: { countries: { country: string; count: number }[] }) {
  const max = countries.length > 0 ? Math.max(...countries.map(c => c.count)) : 1;
  return (
    <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
        <Globe className="h-4.5 w-4.5 text-[#0472A3]" />
        <span>Top Countries</span>
      </h3>
      <div className="space-y-3 pt-1">
        {countries.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No geo data found yet.</p>
        ) : (
          countries.map((c, i) => {
            const pct = (c.count / max) * 100;
            return (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-foreground">{c.country === "Unknown" ? "🌐 Unknown Location" : `📍 ${c.country}`}</span>
                  <span className="text-muted-foreground">{c.count} hits</span>
                </div>
                <div className="w-full bg-muted/40 rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full bg-[#0472A3] transition-all duration-500" style={{ width: `${pct}%` }}></div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function PagePathList({ pages }: { pages: { page_path: string; count: number }[] }) {
  const max = pages.length > 0 ? Math.max(...pages.map(p => p.count)) : 1;
  return (
    <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
        <Activity className="h-4.5 w-4.5 text-[#066E61]" />
        <span>Most Active Routes</span>
      </h3>
      <div className="space-y-3 pt-1">
        {pages.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No pages recorded yet.</p>
        ) : (
          pages.map((p, i) => {
            const pct = (p.count / max) * 100;
            return (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-foreground break-all">{p.page_path}</span>
                  <span className="text-muted-foreground">{p.count} views</span>
                </div>
                <div className="w-full bg-muted/40 rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full bg-[#066E61] transition-all duration-500" style={{ width: `${pct}%` }}></div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function LocalesWidget({ locales }: { locales: { locale: string; count: number }[] }) {
  const total = locales.reduce((sum, item) => sum + item.count, 0) || 1;
  return (
    <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
        <Languages className="h-4.5 w-4.5 text-[#00d973]" />
        <span>Locale Preference</span>
      </h3>
      <div className="space-y-3 pt-1">
        {locales.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No translations loaded yet.</p>
        ) : (
          locales.map((l, i) => {
            const pct = (l.count / total) * 100;
            return (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-foreground">{l.locale.toUpperCase() === "TR" ? "🇹🇷 Turkish (TR)" : "🇺🇸 English (EN)"}</span>
                  <span className="text-muted-foreground">{pct.toFixed(1)}% ({l.count} views)</span>
                </div>
                <div className="w-full bg-muted/40 rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full bg-[#00d973] transition-all duration-500" style={{ width: `${pct}%` }}></div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function SvgTimelineChart({ timeline }: { timeline: { date: string; hits: number; uniques: number }[] }) {
  if (timeline.length === 0) {
    return (
      <div className="h-64 border border-border/60 bg-card/65 rounded-2xl p-6 flex items-center justify-center italic text-xs text-muted-foreground">
        Accumulating timeline telemetry events...
      </div>
    );
  }

  const maxVal = Math.max(...timeline.map((d) => Math.max(d.hits, d.uniques)), 10);
  const width = 600;
  const height = 200;
  const padding = 30;

  const pointsHits = timeline.map((d, i) => {
    const x = padding + (i / (timeline.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - (d.hits / maxVal) * (height - padding * 2);
    return { x, y, date: d.date, value: d.hits };
  });

  const pointsUniques = timeline.map((d, i) => {
    const x = padding + (i / (timeline.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - (d.uniques / maxVal) * (height - padding * 2);
    return { x, y, date: d.date, value: d.uniques };
  });

  const getPathD = (pts: { x: number; y: number }[]) =>
    pts.reduce((acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`), "");

  return (
    <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
      <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
        <span>Visitor Volume Timeline (30 Days)</span>
        <div className="flex gap-4 text-[10px] font-bold">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#0472A3] rounded-full inline-block"></span>Page Views</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#00d973] rounded-full inline-block"></span>Uniques</span>
        </div>
      </h3>

      <div className="w-full overflow-x-auto pt-2">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[500px] h-64 overflow-visible">
          {/* Y Axis Gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
            const y = height - padding - ratio * (height - padding * 2);
            return (
              <g key={idx}>
                <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="3,3" opacity="0.3" />
                <text x={padding - 5} y={y + 4} textAnchor="end" className="fill-muted-foreground text-[8px] font-semibold">
                  {Math.round(ratio * maxVal)}
                </text>
              </g>
            );
          })}

          {/* Paths */}
          <path d={getPathD(pointsHits)} fill="none" stroke="#0472A3" strokeWidth="2" strokeLinecap="round" className="animate-draw" />
          <path d={getPathD(pointsUniques)} fill="none" stroke="#00d973" strokeWidth="2" strokeLinecap="round" className="animate-draw" />

          {/* Hit points */}
          {pointsHits.map((p, idx) => (
            <circle key={`h-${idx}`} cx={p.x} cy={p.y} r="3" className="fill-[#0472A3] hover:r-4.5 cursor-pointer transition-all duration-150">
              <title>{p.date}: {p.value} Page Views</title>
            </circle>
          ))}
          {pointsUniques.map((p, idx) => (
            <circle key={`u-${idx}`} cx={p.x} cy={p.y} r="3" className="fill-[#00d973] hover:r-4.5 cursor-pointer transition-all duration-150">
              <title>{p.date}: {p.value} Uniques</title>
            </circle>
          ))}

          {/* Dates labels on X axis */}
          {timeline.filter((_, i) => i % Math.max(1, Math.floor(timeline.length / 5)) === 0 || i === timeline.length - 1).map((d, i) => {
            const index = timeline.indexOf(d);
            const x = padding + (index / (timeline.length - 1 || 1)) * (width - padding * 2);
            const formatted = d.date.substring(5); // MM-DD
            return (
              <text key={i} x={x} y={height - padding + 15} textAnchor="middle" className="fill-muted-foreground text-[8px] font-bold">
                {formatted}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export function AdminAnalyticsTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalyticsData | null>(null);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await adminFetch("/api/admin/analytics-summary", {});
      if (res?.success) {
        setData(res);
      } else {
        toast.error("Failed to load analytics telemetry");
      }
    } catch {
      toast.error("Failed to connect to API server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3">
        <RefreshCw className="h-8 w-8 text-brand-primary animate-spin" />
        <p className="text-sm font-semibold text-muted-foreground">Aggregating telemetry datasets...</p>
      </div>
    );
  }

  if (!data) return null;

  const { last7Days, last30Days } = data.summary;

  return (
    <div className="space-y-8 animate-fade-in text-left">
      <div className="flex justify-between items-center pb-2 border-b border-border/80">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-brand-primary" />
            <span>Visitor Telemetry</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Real-time, GDPR-compliant cookie-free insights collected from edge requests.
          </p>
        </div>
        <button onClick={fetchSummary} className="p-2 border border-border/60 bg-card rounded-xl hover:bg-muted/30 transition-all">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Hits (7 Days)" value={last7Days.hits} icon={Eye} color="text-blue-500 bg-blue-500/10" />
        <StatCard title="Uniques (7 Days)" value={last7Days.uniques} icon={Users} color="text-emerald-500 bg-emerald-500/10" />
        <StatCard title="Views (30 Days)" value={last30Days.hits} icon={Eye} color="text-purple-500 bg-purple-500/10" />
        <StatCard title="Uniques (30 Days)" value={last30Days.uniques} icon={Users} color="text-amber-500 bg-amber-500/10" />
      </div>

      <SvgTimelineChart timeline={data.timeline} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PagePathList pages={data.topPages} />
        <CountryList countries={data.countries} />
        <LocalesWidget locales={data.locales} />
      </div>
    </div>
  );
}

export default AdminAnalyticsTab;
